"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, ChevronDown, Loader2, Minus, Plus, ShoppingBag, Tag, Trash2, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { applyCoupon, fetchFullCart, removeCartItem, removeCoupon, updateItemQuantity, type FullCartItem, type FullCartView } from "@/lib/cartApi";
import { fetchWooUrls } from "@/integrations/wordpress/cartUtils";
import { getCouponLabel, getFeeLabel } from "@/lib/savingsLabel";
import CartUpsell from "./CartUpsell";

/** Visible height of the WordPress admin bar, or 0 when it is absent. */
function measureWpAdminBar(): number {
  if (typeof document === "undefined") return 0;
  const bar = document.getElementById("wpadminbar");
  if (!bar) return 0;
  const style = window.getComputedStyle(bar);
  if (style.display === "none" || style.visibility === "hidden") return 0;
  return bar.getBoundingClientRect().height;
}
const EMPTY_FULL_CART: FullCartView = {
  itemsCount: 0,
  items: [],
  totals: {
    totalItems: 0,
    totalPrice: 0,
    totalDiscount: 0,
    totalTax: 0,
    totalShipping: 0,
    formattedTotalItems: "$0.00",
    formattedTotalPrice: "$0.00",
    currency: {
      code: "USD",
      symbol: "$",
      prefix: "$",
      suffix: "",
      minorUnit: 2,
      decimalSeparator: ".",
      thousandSeparator: ","
    }
  },
  needsPayment: false,
  needsShipping: false,
  coupons: [],
  fees: [],
  negativeFees: [],
  shipping: {
    known: false
  },
  tax: {
    known: false
  }
};
export interface HeaderCartProps {
  /** Enabled when age/RUO gate finished and visitor is signed in. Fails closed. */
  enabled: boolean;
}
export default function HeaderCart({
  enabled
}: HeaderCartProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [cart, setCart] = useState<FullCartView>(EMPTY_FULL_CART);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyItemKeys, setBusyItemKeys] = useState<Set<string>>(new Set());
  const [isDrawerMutating, setIsDrawerMutating] = useState(false);

  // Promo code block state
  const [promoExpanded, setPromoExpanded] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);

  // Errors passed from external add-to-cart operations (e.g. Build your pack)
  const [addErrors, setAddErrors] = useState<string[]>([]);

  // WooCommerce destination URLs (start empty, populated from WooCommerce API)
  const [cartUrl, setCartUrl] = useState<string>("");
  const [checkoutUrl, setCheckoutUrl] = useState<string>("");
  const [wpAdminBarHeight, setWpAdminBarHeight] = useState(0);

  // Stale request race guard
  const requestIdRef = useRef(0);

  // Serialized mutation queue to guarantee in-order Store API execution
  const mutationQueueRef = useRef<Promise<void>>(Promise.resolve());

  // Flag to suppress duplicate fetch when opening from wvc_cart_updated
  const suppressNextOpenReloadRef = useRef(false);

  // Helper to dispatch wvc_cart_updated marking this drawer as the originator
  const dispatchDrawerCartUpdated = useCallback(() => {
    document.dispatchEvent(new CustomEvent("wvc_cart_updated", {
      detail: {
        source: "header-cart"
      }
    }));
  }, []);

  // 1. Force close drawer if enabled becomes false (e.g. gate opens or user logs out)
  useEffect(() => {
    if (!enabled && isOpen) {
      setIsOpen(false);
    }
  }, [enabled, isOpen]);

  // 2. Measure WP Admin bar on mount and resize
  useEffect(() => {
    const handleResize = () => setWpAdminBarHeight(measureWpAdminBar());
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 3. Fetch Woo URLs once on mount
  useEffect(() => {
    void fetchWooUrls().then(urls => {
      if (urls.cart && urls.cart !== "#") {
        setCartUrl(urls.cart);
      }
      if (urls.checkout && urls.checkout !== "#") {
        setCheckoutUrl(urls.checkout);
      }
    });
  }, []);

  // 4. Cart fetcher with request id protection
  const reloadCart = useCallback(async (showFullSpinner = false) => {
    if (!enabled) return;
    const reqId = ++requestIdRef.current;
    if (showFullSpinner) {
      setInitialLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setLoadError(null);
    try {
      const freshCart = await fetchFullCart();
      if (reqId !== requestIdRef.current) return;
      setCart(freshCart);
      setLoadError(null);
    } catch (err: any) {
      if (reqId !== requestIdRef.current) return;
      console.error("[HeaderCart] Cart load failed:", err);
      setLoadError(err?.message || "Unable to load your cart. Please try again.");
    } finally {
      if (reqId === requestIdRef.current) {
        setInitialLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [enabled]);

  // Initial fetch on mount once enabled
  useEffect(() => {
    if (enabled) {
      void reloadCart(true);
    } else {
      setCart(EMPTY_FULL_CART);
      setInitialLoading(false);
    }
  }, [enabled, reloadCart]);

  // 5. React to wvc_cart_updated event -> refresh and OPEN drawer once
  useEffect(() => {
    const handleCartUpdated = (e: Event) => {
      if (!enabled) return;

      // Ignore events dispatched by HeaderCart itself
      const customEvent = e as CustomEvent<{
        source?: string;
        addErrors?: string[];
      }>;
      if (customEvent.detail && customEvent.detail.source === "header-cart") {
        return;
      }

      // Check for addErrors from caller (e.g. Build your pack)
      if (customEvent.detail && Array.isArray(customEvent.detail.addErrors) && customEvent.detail.addErrors.length > 0) {
        setAddErrors(customEvent.detail.addErrors);
      } else {
        setAddErrors([]);
      }

      // Prevent isOpen effect from firing duplicate reload immediately
      suppressNextOpenReloadRef.current = true;
      void reloadCart(false);
      setIsOpen(true);
    };
    document.addEventListener("wvc_cart_updated", handleCartUpdated);
    return () => {
      document.removeEventListener("wvc_cart_updated", handleCartUpdated);
    };
  }, [enabled, reloadCart]);

  // 6. Refresh on drawer open (re-check admin bar height as well)
  useEffect(() => {
    if (isOpen && enabled) {
      setWpAdminBarHeight(measureWpAdminBar());
      if (suppressNextOpenReloadRef.current) {
        suppressNextOpenReloadRef.current = false;
      } else {
        void reloadCart(false);
      }
    }
  }, [isOpen, enabled, reloadCart]);

  // 7. Debounced refresh on window focus, visibilitychange, pageshow (without opening)
  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const triggerDebouncedReload = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void reloadCart(false);
      }, 300);
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        triggerDebouncedReload();
      }
    };
    window.addEventListener("focus", triggerDebouncedReload);
    window.addEventListener("pageshow", triggerDebouncedReload);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("focus", triggerDebouncedReload);
      window.removeEventListener("pageshow", triggerDebouncedReload);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enabled, reloadCart]);

  /* ------------------------------------------------------------------------ */
  /* SERIALIZED MUTATION RUNNER                                               */
  /* ------------------------------------------------------------------------ */

  const enqueueMutation = useCallback(<T,>(operation: () => Promise<FullCartView>): Promise<FullCartView | null> => {
    return new Promise(resolve => {
      mutationQueueRef.current = mutationQueueRef.current.then(async () => {
        setIsDrawerMutating(true);
        const mutId = ++requestIdRef.current;
        try {
          const nextCart = await operation();
          requestIdRef.current = ++mutId;
          setCart(nextCart);
          setLoadError(null);
          resolve(nextCart);
        } catch (err: any) {
          console.error("[HeaderCart] Mutation failed:", err);
          try {
            const recoveredCart = await fetchFullCart();
            requestIdRef.current = ++mutId;
            setCart(recoveredCart);
            setLoadError(null);
          } catch (recoveryErr) {
            console.warn("[HeaderCart] Post-mutation cart recovery failed:", recoveryErr);
          }
          resolve(null);
          throw err;
        } finally {
          setIsDrawerMutating(false);
        }
      }).catch(() => {
        // Keep promise chain unbroken for subsequent operations
      });
    });
  }, []);
  const setItemBusy = (key: string, isBusy: boolean) => {
    setBusyItemKeys(prev => {
      const next = new Set(prev);
      if (isBusy) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };
  const handleUpdateQuantity = async (item: FullCartItem, nextQuantity: number) => {
    if (isDrawerMutating) return;
    if (nextQuantity <= 0) {
      await handleRemoveItem(item.key);
      return;
    }
    const limits = item.quantityLimits;
    if (typeof limits.max === "number" && nextQuantity > limits.max) {
      setActionError(`Maximum quantity of ${limits.max} reached for ${item.name}.`);
      return;
    }
    if (nextQuantity < limits.min) {
      setActionError(`Minimum quantity is ${limits.min}.`);
      return;
    }
    setItemBusy(item.key, true);
    setActionError(null);
    try {
      const result = await enqueueMutation(() => updateItemQuantity(item.key, nextQuantity));
      if (result) {
        dispatchDrawerCartUpdated();
      }
    } catch (err: any) {
      console.error("[HeaderCart] Update item quantity failed:", err);
      setActionError(err?.message || "Failed to update quantity. Please try again.");
    } finally {
      setItemBusy(item.key, false);
    }
  };
  const handleRemoveItem = async (key: string) => {
    if (isDrawerMutating) return;
    setItemBusy(key, true);
    setActionError(null);
    try {
      const result = await enqueueMutation(() => removeCartItem(key));
      if (result) {
        dispatchDrawerCartUpdated();
      }
    } catch (err: any) {
      console.error("[HeaderCart] Remove item failed:", err);
      setActionError(err?.message || "Failed to remove item. Please try again.");
    } finally {
      setItemBusy(key, false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /* PROMO CODE HANDLERS                                                      */
  /* ------------------------------------------------------------------------ */

  const handleApplyPromo = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = promoInput.trim();
    if (!code) {
      setPromoError("Please enter a promo code.");
      return;
    }
    if (isDrawerMutating || promoBusy) return;
    setPromoBusy(true);
    setPromoError(null);
    setActionError(null);
    try {
      const result = await enqueueMutation(() => applyCoupon(code));
      if (result) {
        setPromoInput("");
        setPromoExpanded(false);
        dispatchDrawerCartUpdated();
      }
    } catch (err: any) {
      console.error("[HeaderCart] Apply coupon failed:", err);
      setPromoError(err?.message || "Unable to apply promo code.");
    } finally {
      setPromoBusy(false);
    }
  };
  const handleRemoveCoupon = async (code: string) => {
    if (isDrawerMutating || promoBusy) return;
    setPromoBusy(true);
    setPromoError(null);
    setActionError(null);
    try {
      const result = await enqueueMutation(() => removeCoupon(code));
      if (result) {
        dispatchDrawerCartUpdated();
      }
    } catch (err: any) {
      console.error("[HeaderCart] Remove coupon failed:", err);
      setPromoError(err?.message || "Unable to remove promo code.");
    } finally {
      setPromoBusy(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /* CALCULATED DISPLAY VALUES                                                */
  /* ------------------------------------------------------------------------ */

  const cartQuantitiesByProductId = useMemo(() => {
    const map: Record<number, number> = {};
    for (const item of cart.items) {
      if (item.id) {
        map[item.id] = (map[item.id] ?? 0) + item.quantity;
      }
    }
    return map;
  }, [cart.items]);
  if (!enabled) {
    return null;
  }
  const badgeCount = cart.itemsCount > 0 ? cart.itemsCount : cart.items.reduce((sum, item) => sum + item.quantity, 0);
  const displayBadge = !initialLoading && badgeCount > 0;
  const countLabel = badgeCount > 99 ? "99+" : String(badgeCount);
  const itemsCountText = `${badgeCount} ${badgeCount === 1 ? "item" : "items"}`;
  const hasCoupons = cart.coupons && cart.coupons.length > 0;
  const negativeFees = cart.negativeFees || [];
  const savingsCtx = {
    preDiscountItemsSubtotalMinor: cart.totals.totalItemsMinor,
    totalItemsMinor: cart.totals.totalItemsMinor,
    totalDiscountMinor: cart.totals.totalDiscountMinor
  };
  return <>
      {/* -------------------------------------------------------------------- */}
      {/* HEADER TRIGGER BUTTON                                                */}
      {/* -------------------------------------------------------------------- */}
      <Button type="button" variant="ghost" size="icon" className="relative overflow-visible rounded-full text-[#172126] hover:bg-[#e9f1f4] hover:text-[#087db3]" aria-label={badgeCount > 0 ? `Shopping cart, ${badgeCount} ${badgeCount === 1 ? "item" : "items"}` : "Shopping cart"} aria-haspopup="dialog" aria-expanded={isOpen} onClick={() => setIsOpen(true)}>
        <ShoppingBag className="size-4" aria-hidden="true" />

        {displayBadge && <span className="absolute -top-1 -right-1 z-10 flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#168fc4] px-1 text-[10px] font-bold leading-none text-white shadow-sm ring-2 ring-white" aria-hidden="true">
            {countLabel}
          </span>}
      </Button>

      {/* -------------------------------------------------------------------- */}
      {/* SIDE CART DRAWER (SHEET)                                             */}
      {/* -------------------------------------------------------------------- */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="right" className="!fixed !z-50 flex h-[100dvh] w-[94vw] max-w-[440px] flex-col overflow-hidden !rounded-l-[24px] border-l border-[#dbe6eb] !bg-white p-0 text-[#172126] shadow-2xl transition-transform duration-300 ease-in-out [&>button]:hidden" style={wpAdminBarHeight > 0 ? {
        top: wpAdminBarHeight,
        height: `calc(100dvh - ${wpAdminBarHeight}px)`
      } : undefined}>
          {/* Accessible Title & Description for screen readers */}
          <SheetHeader className="sr-only">
            <SheetTitle>Your cart</SheetTitle>
            <SheetDescription>
              {badgeCount === 0 ? "Your cart is currently empty" : `${itemsCountText} in your cart`}
            </SheetDescription>
          </SheetHeader>

          {/* Top Drawer Header with single visible accessible close button */}
          <div className="shrink-0 border-b border-[#e5edf0] bg-[#f8fafb] px-5 py-4 sm:px-6">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-baseline gap-2.5">
                <h2 className="text-lg font-semibold tracking-[-0.02em] text-[#111827] sm:text-xl">
                  Your cart
                </h2>
                {!initialLoading && <span className="text-xs font-medium text-[#64748b]">
                    ({itemsCountText})
                  </span>}
              </div>

              <button type="button" onClick={() => setIsOpen(false)} className="flex size-9 items-center justify-center rounded-full text-[#4b5563] transition-colors hover:bg-[#e9f1f4] hover:text-[#0f172a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#168fc4]" aria-label="Close cart">
                <X className="size-4" aria-hidden="true" />
              </button>
            </div>

            {/* Ocean-blue accent bar */}
            <div className="mt-3.5 h-[2px] w-full rounded-full bg-gradient-to-r from-[#159fdf] via-[#168fc4] to-[#087db3]/40" aria-hidden="true" />

            {/* Refreshing animated progress bar (when items already exist in cart or drawer is mutating) */}
            {(isRefreshing || isDrawerMutating) && cart.items.length > 0 && <div className="relative mt-1 h-0.5 w-full overflow-hidden rounded-full bg-[#e2eaf0]" aria-hidden="true">
                <div className="h-full w-2/5 animate-pulse rounded-full bg-[#168fc4] transition-all" />
              </div>}
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* DRAWER BODY (SCROLLABLE PRODUCT LIST / LOADING / EMPTY / ERROR)  */}
          {/* ---------------------------------------------------------------- */}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6" aria-busy={initialLoading || isRefreshing || isDrawerMutating}>
            {/* Add errors notice (from external add-to-cart operations) */}
            {addErrors.length > 0 && <div role="alert" className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 size-4 shrink-0 text-red-600" aria-hidden="true" />
                  <div className="space-y-1">
                    <p className="font-semibold text-red-900">Some items could not be added:</p>
                    <ul className="list-disc pl-4 space-y-0.5 text-red-700">
                      {addErrors.map((msg, idx) => <li key={idx}>{msg}</li>)}
                    </ul>
                  </div>
                </div>
                <button type="button" onClick={() => setAddErrors([])} className="rounded px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-red-400">
                  Dismiss
                </button>
              </div>}

            {/* Background refresh error banner when items are already shown */}
            {loadError && cart.items.length > 0 && <div role="status" className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <div className="flex items-center gap-2">
                  <AlertCircle className="size-4 shrink-0 text-amber-600" aria-hidden="true" />
                  <span>Couldn't refresh totals.</span>
                </div>
                <button type="button" onClick={() => void reloadCart(false)} className="font-semibold text-[#087db3] hover:underline">
                  Retry
                </button>
              </div>}

            {initialLoading || isRefreshing && cart.items.length === 0 ? <div className="flex h-full flex-col items-center justify-center gap-3 py-16 text-center text-[#64748b]" aria-live="polite" aria-busy="true">
                <Loader2 className="size-7 animate-spin text-[#168fc4]" aria-hidden="true" />
                <p className="text-sm font-medium">
                  {isRefreshing && !initialLoading ? "Updating your cart..." : "Loading your cart..."}
                </p>
              </div> : loadError && cart.items.length === 0 ? <div role="alert" className="flex h-full flex-col items-center justify-center gap-4 py-16 text-center">
                <div className="flex size-12 items-center justify-center rounded-full bg-red-50 text-red-600">
                  <AlertCircle className="size-6" aria-hidden="true" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-[#111827]">Unable to load cart</p>
                  <p className="text-xs text-[#64748b]">{loadError}</p>
                </div>
                <Button type="button" onClick={() => reloadCart(true)} className="rounded-full bg-[#168fc4] px-5 text-xs text-white hover:bg-[#117eae]">
                  Try again
                </Button>
              </div> : cart.items.length === 0 ? <div className="flex h-full flex-col items-center justify-center gap-4 py-16 text-center">
                <div className="flex size-16 items-center justify-center rounded-full border border-[#dce8ed] bg-[#f0f6f9] text-[#168fc4]">
                  <ShoppingBag className="size-8" aria-hidden="true" />
                </div>
                <div className="space-y-1.5">
                  <p className="text-base font-semibold text-[#111827]">Your cart is empty</p>
                  <p className="max-w-[260px] text-xs text-[#64748b]">
                    Explore verified research-use-only laboratory materials in our catalog.
                  </p>
                </div>
                <Button asChild className="mt-2 rounded-full bg-[#168fc4] px-6 text-sm font-medium text-white shadow-sm hover:bg-[#117eae]" onClick={() => setIsOpen(false)}>
                  <Link to="/ShopPage">Continue shopping</Link>
                </Button>
              </div> : (/* PRODUCT ROWS */
          <>
                <ul className="divide-y divide-[#edf2f5]" aria-label="Cart items">
                  {cart.items.map(item => {
                const isItemBusy = busyItemKeys.has(item.key) || isDrawerMutating;
                const limits = item.quantityLimits;
                const canDecrement = limits.editable && item.quantity - limits.multipleOf >= limits.min;
                const canIncrement = limits.editable && (typeof limits.max !== "number" || item.quantity + limits.multipleOf <= limits.max);
                return <li key={item.key} className={`relative flex items-start gap-3.5 py-4 transition-opacity ${isItemBusy ? "opacity-60" : ""}`}>
                        {/* 64px rounded thumbnail */}
                        <div className="size-16 shrink-0 overflow-hidden rounded-lg border border-[#e2eaf0] bg-[#f8fafb]">
                          {item.imageSrc ? <img src={item.imageSrc} alt={item.imageAlt || item.name} className="size-full object-cover" loading="lazy" decoding="async" /> : <div className="flex size-full items-center justify-center text-[#94a3b8]">
                              <ShoppingBag className="size-6" aria-hidden="true" />
                            </div>}
                        </div>

                        {/* Product details */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              {item.permalink ? <Link href={item.permalink} onClick={() => setIsOpen(false)} className="line-clamp-2 text-sm font-semibold text-[#111827] transition-colors hover:text-[#087db3]">
                                  {item.name}
                                </Link> : <p className="line-clamp-2 text-sm font-semibold text-[#111827]">
                                  {item.name}
                                </p>}

                              {/* Strength & Variation attributes */}
                              {item.rawVariationLabels && item.rawVariationLabels.length > 0 && <div className="mt-1 flex flex-wrap gap-1">
                                  {item.rawVariationLabels.map((v, i) => <span key={i} className="inline-flex items-center rounded-md border border-[#d8e5ec] bg-[#f4f9fb] px-1.5 py-0.5 text-[11px] font-medium text-[#4b5563]">
                                      {v.attribute}: {v.value}
                                    </span>)}
                                </div>}

                              {/* Unit Price (with strike-through regular price if lower from server) */}
                              <div className="mt-1 flex items-baseline gap-1.5 font-mono text-xs">
                                {item.hasDiscountedUnitPrice && item.regularUnitFormatted && <span className="text-[#94a3b8] line-through">
                                    {item.regularUnitFormatted}
                                  </span>}
                                <span className="font-semibold text-[#0f172a]">
                                  {item.formattedUnitPrice} each
                                </span>
                              </div>
                            </div>

                            {/* Line Total */}
                            <div className="shrink-0 text-right">
                              <span className="font-mono text-sm font-bold text-[#111827]">
                                {item.formattedLineTotal}
                              </span>
                            </div>
                          </div>

                          {/* Quantity Stepper & Remove text link */}
                          <div className="mt-3 flex items-center justify-between gap-3">
                            <div className="inline-flex items-center rounded-lg border border-[#cfdce3] bg-white shadow-2xs" role="group" aria-label={`Quantity selector for ${item.name}`}>
                              <button type="button" onClick={() => handleUpdateQuantity(item, item.quantity - limits.multipleOf)} disabled={!canDecrement || isItemBusy} className="flex size-8 items-center justify-center text-[#475569] transition-colors hover:bg-[#f1f5f9] hover:text-[#0f172a] disabled:cursor-not-allowed disabled:opacity-30" aria-label={`Decrease quantity of ${item.name}`}>
                                <Minus className="size-3.5" aria-hidden="true" />
                              </button>

                              <span className="min-w-[28px] text-center font-mono text-xs font-semibold text-[#111827]">
                                {item.quantity}
                              </span>

                              <button type="button" onClick={() => handleUpdateQuantity(item, item.quantity + limits.multipleOf)} disabled={!canIncrement || isItemBusy} className="flex size-8 items-center justify-center text-[#475569] transition-colors hover:bg-[#f1f5f9] hover:text-[#0f172a] disabled:cursor-not-allowed disabled:opacity-30" aria-label={`Increase quantity of ${item.name}`}>
                                <Plus className="size-3.5" aria-hidden="true" />
                              </button>
                            </div>

                            <button type="button" onClick={() => handleRemoveItem(item.key)} disabled={isItemBusy} className="inline-flex min-h-[36px] items-center gap-1 text-xs font-medium text-[#64748b] transition-colors hover:text-red-600 focus-visible:outline-none focus-visible:underline disabled:cursor-not-allowed disabled:opacity-40" aria-label={`Remove ${item.name} from cart`}>
                              <Trash2 className="size-3.5" aria-hidden="true" />
                              <span>Remove</span>
                            </button>
                          </div>
                        </div>
                      </li>;
              })}
                </ul>
                <CartUpsell cartProductIds={cart.items.map(i => i.id)} cartQuantitiesByProductId={cartQuantitiesByProductId} onNavigate={() => setIsOpen(false)} busy={isRefreshing || isDrawerMutating || busyItemKeys.size > 0} />
              </>)}
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* PINNED FOOTER (PROMO BLOCK + TOTALS + ACTIONS)                   */}
          {/* ---------------------------------------------------------------- */}
          {cart.items.length > 0 && <div className="shrink-0 border-t border-[#e2eaf0] bg-[#ffffff] px-5 pt-3.5 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6" aria-busy={isRefreshing || isDrawerMutating}>
              {/* Mutation action error message */}
              {actionError && <div role="alert" className="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
                  <AlertCircle className="mt-0.5 size-4 shrink-0 text-red-600" aria-hidden="true" />
                  <span className="flex-1 leading-relaxed">{actionError}</span>
                </div>}

              {/* Promo Code Section (collapsible) */}
              <div className="mb-3 border-b border-[#eef2f5] pb-3">
                <button type="button" onClick={() => setPromoExpanded(prev => !prev)} className="flex w-full items-center justify-between text-left text-xs font-medium text-[#087db3] transition-colors hover:text-[#117eae]" aria-expanded={promoExpanded} aria-controls="cart-promo-accordion">
                  <span className="flex items-center gap-1.5">
                    <Tag className="size-3.5" aria-hidden="true" />
                    <span>Have a promo code?</span>
                  </span>
                  <ChevronDown className={`size-3.5 transition-transform duration-200 ${promoExpanded ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>

                {promoExpanded && <form id="cart-promo-accordion" onSubmit={handleApplyPromo} className="mt-2.5 space-y-2">
                    <div className="flex gap-2">
                      <input type="text" value={promoInput} onChange={e => {
                  setPromoInput(e.target.value);
                  if (promoError) setPromoError(null);
                }} placeholder="Promo code" disabled={promoBusy || isDrawerMutating} className="h-10 flex-1 rounded-lg border border-[#cbd9df] bg-white px-3 text-base text-[#111827] placeholder-[#94a3b8] outline-none transition-[border-color,box-shadow] focus:border-[#168fc4] focus:ring-1 focus:ring-[#168fc4] sm:text-sm" />
                      <Button type="submit" disabled={promoBusy || isDrawerMutating || !promoInput.trim()} className="h-10 rounded-lg bg-[#168fc4] px-4 text-xs font-semibold text-white hover:bg-[#117eae] disabled:opacity-50">
                        {promoBusy ? <Loader2 className="size-3.5 animate-spin" aria-hidden="true" /> : "Apply"}
                      </Button>
                    </div>

                    {promoError && <p role="alert" className="text-xs font-medium text-red-600">
                        {promoError}
                      </p>}
                  </form>}

                {/* Applied Coupons chips */}
                {hasCoupons && <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {cart.coupons.map(coupon => {
                const friendlyLabel = getCouponLabel(coupon, savingsCtx);
                return <span key={coupon.code} className="inline-flex items-center gap-1.5 rounded-full border border-[#bcd8e5] bg-[#eaf5fa] px-2.5 py-1 text-xs font-medium text-[#087db3]">
                          <Tag className="size-3" aria-hidden="true" />
                          <span>{friendlyLabel}</span>
                          {coupon.discountFormatted && <span className="text-[#0369a1]">
                              (-{coupon.discountFormatted})
                            </span>}
                          <button type="button" onClick={() => handleRemoveCoupon(coupon.code)} disabled={promoBusy || isDrawerMutating} className="ml-0.5 flex size-4 items-center justify-center rounded-full text-[#087db3] transition-colors hover:bg-[#d5ebf5] hover:text-[#0369a1] disabled:opacity-50" aria-label={`Remove coupon ${friendlyLabel || coupon.code}`}>
                            <X className="size-3" aria-hidden="true" />
                          </button>
                        </span>;
              })}
                  </div>}
              </div>

              {/* Totals Summary */}
              <div className={`space-y-1.5 font-sans text-xs transition-opacity duration-150 ${isRefreshing || isDrawerMutating ? "opacity-60" : "opacity-100"}`}>
                {/* Subtotal */}
                <div className="flex items-center justify-between text-[#4b5563]">
                  <span>Subtotal</span>
                  <span className="font-mono text-sm font-medium text-[#111827]">
                    {cart.totals.formattedTotalItems}
                  </span>
                </div>

                {/* Coupon Discounts if applied */}
                {hasCoupons && cart.coupons.map(c => {
              const couponLabel = getCouponLabel(c, savingsCtx);
              return <div key={c.code} className="flex items-center justify-between text-emerald-700">
                        <span className="flex items-center gap-1">
                          <span>Coupon ({couponLabel})</span>
                        </span>
                        <span className="font-mono font-medium">
                          -{c.discountFormatted}
                        </span>
                      </div>;
            })}

                {/* Negative Fees (Quantity tier savings from WooCommerce server snippet) */}
                {negativeFees.map(fee => {
              const feeLabel = getFeeLabel(fee, savingsCtx);
              return <div key={fee.id} className="flex items-center justify-between text-emerald-700">
                      <span>{feeLabel}</span>
                      <span className="font-mono font-medium">{fee.formatted}</span>
                    </div>;
            })}

                {/* Shipping */}
                <div className="flex items-center justify-between text-[#4b5563]">
                  <span>
                    Shipping
                    {cart.shipping.selectedRateName ? ` (${cart.shipping.selectedRateName})` : ""}
                  </span>
                  <span className="font-medium text-[#111827]">
                    {cart.shipping.known && cart.shipping.totalFormatted ? cart.shipping.totalFormatted : "Calculated at checkout"}
                  </span>
                </div>

                {/* Tax */}
                <div className="flex items-center justify-between text-[#4b5563]">
                  <span>Estimated Tax</span>
                  <span className="font-medium text-[#111827]">
                    {cart.tax.known && cart.tax.totalFormatted ? cart.tax.totalFormatted : "Calculated at checkout"}
                  </span>
                </div>

                {/* Total */}
                <div className="mt-2.5 flex items-baseline justify-between border-t border-[#edf2f5] pt-2.5 text-sm font-semibold text-[#111827]">
                  <span className="text-base font-bold">Total</span>
                  <div className="text-right">
                    <span className="font-mono text-lg font-bold text-[#111827]">
                      {cart.totals.formattedTotalPrice}
                    </span>
                    <p className="text-[10px] font-normal text-[#64748b]">
                      Final shipping and tax are confirmed at checkout.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-4 space-y-2.5">
                {/* Checkout Primary Button */}
                {checkoutUrl ? <Link href={checkoutUrl} onClick={() => setIsOpen(false)} className="flex h-12 w-full items-center justify-center rounded-xl bg-[#168fc4] px-6 text-sm font-semibold text-white shadow-sm transition-[background-color,box-shadow] hover:bg-[#117eae] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#168fc4] focus-visible:ring-offset-2">
                    Continue to checkout
                  </Link> : <Link to="/CheckoutPage" onClick={() => setIsOpen(false)} className="flex h-12 w-full items-center justify-center rounded-xl bg-[#168fc4] px-6 text-sm font-semibold text-white shadow-sm transition-[background-color,box-shadow] hover:bg-[#117eae] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#168fc4] focus-visible:ring-offset-2">
                    Continue to checkout
                  </Link>}

                {/* Secondary navigation links */}
                <div className="flex items-center justify-center gap-4 text-xs font-medium text-[#64748b]">
                  {cartUrl ? <Link href={cartUrl} onClick={() => setIsOpen(false)} className="transition-colors hover:text-[#087db3] hover:underline">
                      View cart
                    </Link> : <Link to="/CartPage" onClick={() => setIsOpen(false)} className="transition-colors hover:text-[#087db3] hover:underline">
                      View cart
                    </Link>}

                  <span className="text-[#cbd5e1]" aria-hidden="true">
                    •
                  </span>

                  <button type="button" onClick={() => setIsOpen(false)} className="transition-colors hover:text-[#087db3] hover:underline">
                    Continue shopping
                  </button>
                </div>
              </div>
            </div>}
        </SheetContent>
      </Sheet>
    </>;
}